import { clamp, round } from "lodash-es";

import { captureMessage } from "@/error/sentry";
import { isEqual } from "@/math/coordinates";
import type { Board } from "@/model/board";
import { isBacklogBoard } from "@/model/board";
import type { CardMeta } from "@/model/card";
import { relativeCoord } from "@/model/coordinates";
import type { LinkedObjective } from "@/model/objective";
import { useAlmItemTypeStore } from "@/store/almItemType";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import type { CardEvent } from "@/store/card";
import { useCardStore } from "@/store/card";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";
import { firstValue } from "@/utils/object";
import { parseStickerString, stickerToString } from "@/utils/sticker";

import type { ServerSticky } from "../serverModel";
import { mapLinkedSticky, mapReactions, mapStatusDistribution, mapUserId } from "./mapBackendData";

export function serverStickyPos(data: Partial<ServerSticky>) {
  if (data.a_center) {
    return relativeCoord(data.a_center[0], 1 - data.a_center[1]);
  }
}

export function changeSticky(board: Board, id?: string, data?: Partial<ServerSticky>) {
  if (id && data) {
    const card = board.cards[id];
    if (!card) {
      return;
    }
    const pos = serverStickyPos(data);
    if (!isBacklogBoard(board.type) && pos && !isEqual(card.meta.pos, pos)) {
      useBoardsStore().setCardPos({ id, boardId: board.id, ...pos });
    }
    if (data.stack_pos !== undefined && data.stack_pos !== card.meta.zIndex) {
      useBoardsStore().cardToFront({ id, boardId: board.id, zIndex: data.stack_pos });
    }

    const cardStore = useCardStore();
    const exist = card.data;
    if (data.text !== undefined && data.text !== exist.text) {
      cardStore.setText({ id, text: data.text });
    }
    if (data.type_id !== undefined && data.type_id !== exist.type.id) {
      cardStore.setType({ id, type: data.type_id });
    }
    if (
      !card.data.type.priorities &&
      data.WSJF_value !== undefined &&
      data.WSJF_value !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: data.WSJF_value });
    }
    if (
      card.data.type.priorities &&
      data.step_value !== undefined &&
      data.step_value !== exist.priority
    ) {
      cardStore.setPriority({ id, priority: data.step_value });
    }
    if (data.story_points !== undefined && data.story_points !== exist.points) {
      cardStore.setPoints({ id, points: data.story_points || 0 });
    }
    if (data.status !== undefined && data.status !== exist.status?.name) {
      cardStore.setStatus(board, id, data.status, { statusClass: data.status_class });
    }
    if (data.team_id !== undefined) {
      const teamId = data.team_id ? "" + data.team_id : null;
      if (teamId !== exist.teamId) {
        cardStore.setTeam({ id, teamId });
      }
    }
    if (data.art_id !== undefined) {
      const artId = data.art_id?.toString() ?? null;
      if (artId !== exist.artId) {
        cardStore.setArt({ id, artId });
      }
    }
    if (
      data.iteration_number !== undefined &&
      // yes null or undefined
      // eslint-disable-next-line eqeqeq
      data.iteration_number != exist.iterationId
    ) {
      cardStore.setIteration({ id, iterationId: data.iteration_number });
    }
    // Update almId and almIssueUrl if they have changed
    // (an updated almId forces an update to the almIssueUrl too)
    if (data.alm_issue_id !== undefined && "" + data.alm_issue_id !== exist.almId) {
      cardStore.setAlmId({ id, almId: "" + data.alm_issue_id });
    }
    if (data.alm_issue_url !== undefined && data.alm_issue_url !== exist.almIssueUrl) {
      cardStore.setAlmIssueUrl({ id, almIssueUrl: data.alm_issue_url });
    }
    if (data.alm_source !== undefined && data.alm_source !== exist.almSourceId) {
      cardStore.setAlmSource({ id, almSourceId: data.alm_source });
    }
    if (data.sticky_group !== undefined && data.sticky_group !== exist.groupId) {
      cardStore.setGroup({ id, groupId: data.sticky_group });
    }
    if (data.flag_type !== undefined && data.flag_type !== stickerToString(exist.flagType)) {
      cardStore.setFlag({ id, flagType: parseStickerString(data.flag_type) });
    }
    if (data.risk !== undefined && data.risk !== exist.risk) {
      cardStore.setRisk({ id, risk: data.risk });
    }
    //TODO can data.precond_team_id really be null? Verify and remove the null logic if possible
    if (
      data.precond_team_id !== undefined &&
      isTeamIdDifferent(data.precond_team_id, exist.precondTeam?.id)
    ) {
      cardStore.setPrecondTeam({
        id,
        teamId: data.precond_team_id !== null ? "" + data.precond_team_id : undefined,
      });
    }
    if (data.precond_team !== undefined && !exist.precondTeam) {
      cardStore.setPrecondTeam({ id, teamName: data.precond_team });
    }
    //TODO can data.depend_team_id really be null? Verify and remove the null logic if possible
    if (
      data.depend_team_id !== undefined &&
      isTeamIdDifferent(data.depend_team_id, exist.dependTeam?.id)
    ) {
      cardStore.setDependTeam({
        id,
        teamId: data.depend_team_id !== null ? "" + data.depend_team_id : undefined,
      });
    }
    if (data.depend_team !== undefined && !exist.dependTeam) {
      cardStore.setDependTeam({ id, teamName: data.depend_team });
    }
    if (data.objectives !== undefined) {
      cardStore.setObjectives(id, validateObjectives(data.objectives));
    }
    if (data.reactions !== undefined) {
      cardStore.setReactions(id, mapReactions(data.reactions));
    }
    if (data.assignee !== undefined) {
      cardStore.setAssignee(id, mapUserId(data.assignee?.pip_id, data.assignee?.alm_id));
    }
    if (data.reporter !== undefined) {
      cardStore.setReporter(id, mapUserId(data.reporter?.pip_id, data.reporter?.alm_id));
    }
    if (data.linked_stickies) {
      cardStore.setLinkedStickies(id, data.linked_stickies.map(mapLinkedSticky));
    }
    if (Object.hasOwn(data, "status_distribution")) {
      // When the sticky type is changed, the status distribution might have to be removed
      // so the backend will pass { status_distribution: undefined }
      cardStore.setStatusDistribution(
        id,
        data.status_distribution ? mapStatusDistribution(data.status_distribution) : undefined,
      );
    }
  }
}

export function addSticky(data: ServerSticky & Pick<CardMeta, "shouldAnimate">) {
  const boardsStore = useBoardsStore();
  const board = boardsStore.boards[data.board_id];

  // Log if board doesn't exist in store (for diagnostics)
  // This can legitimately happen when loading linked stickies from unvisited boards
  if (!board) {
    const availableBoardIds = Object.keys(boardsStore.boards);
    const availableBoardTypes = Object.values(boardsStore.boards).map((b) => `${b.id}:${b.type}`);
    const currentBoard = useBoardStore().board;
    const currentSession = useSessionStore().current;
    captureMessage("addSticky: board not found in store", {
      info: {
        missingBoardId: data.board_id,
        stickyId: data.id,
        stickyGroupId: data.sticky_group,
        stickyTypeId: data.type_id,
        availableBoardCount: availableBoardIds.length,
        availableBoardIds,
        availableBoardTypes,
        // Context to help identify race conditions
        currentBoardId: currentBoard?.id,
        currentBoardType: currentBoard?.type,
        currentSessionId: currentSession?.id,
      },
    });
    // Continue adding the sticky - it will have almSourceId: null if board doesn't exist
  }

  const pos = data.a_center || [0.5, 0.5];
  const type = useStickyTypeStore().findStickyType(data.type_id);
  const almSourceId = data.alm_source ?? board?.almSources?.[0]?.id;
  const teamId = data.team_id ? data.team_id.toString() : null;
  const artId = data.art_id ? data.art_id.toString() : null;

  const event: CardEvent = {
    id: data.id,
    groupId: data.sticky_group,
    text: data.text || "",
    status: useAlmItemTypeStore().calcStatus(
      data.status,
      type,
      teamId,
      artId,
      almSourceId,
      data.status_class,
    ),
    points: data.story_points || 0,
    boardId: data.board_id,
    type,
    teamId,
    iterationId: data.iteration_number,
    pos: relativeCoord(clamp(pos[0], 0, 1), 1 - clamp(pos[1], 0, 1)),
    zIndex: data.stack_pos,
    priority: type.priorities
      ? type.priorities[data.step_value!]?.value || firstValue(type.priorities).value
      : round(data.WSJF_value || 0, 2),
    precondTeam: useTeamStore().findTeam({
      id: "" + data.precond_team_id,
      name: data.precond_team,
      withExternal: true,
    }),
    dependTeam: useTeamStore().findTeam({
      id: "" + data.depend_team_id,
      name: data.depend_team,
      withExternal: true,
    }),
    flagType: parseStickerString(data.flag_type),
    almId: data.alm_issue_id === undefined ? undefined : "" + data.alm_issue_id,
    almSourceId,
    almIssueUrl: data.alm_issue_url,
    risk: data.risk,
    shouldAnimate: data.shouldAnimate,
    objectives: validateObjectives(data.objectives),
    reactions: data.reactions ? mapReactions(data.reactions) : undefined,
    artId,
    assignee: mapUserId(data.assignee?.pip_id, data.assignee?.alm_id),
    reporter: mapUserId(data.reporter?.pip_id, data.reporter?.alm_id),
    linkedStickies: data.linked_stickies?.map(mapLinkedSticky) || [],
    statusDistribution: data.status_distribution
      ? mapStatusDistribution(data.status_distribution)
      : undefined,
  };
  useCardStore().add(event);
}

function validateObjectives(objectives: ServerSticky["objectives"]): LinkedObjective[] {
  if (!objectives) {
    return [];
  }
  return objectives.flatMap((objective) => {
    if (objective.id && objective.board_id) {
      return { id: objective.id, boardId: objective.board_id };
    }
    captureMessage("Received invalid objective", { info: { objective } });
    return [];
  });
}

function isTeamIdDifferent(numericTeamId: number | null, stringTeamId: string | undefined) {
  return numericTeamId === null ? stringTeamId !== undefined : "" + numericTeamId !== stringTeamId;
}
